import { defineStore } from 'pinia';
import * as BatchRepo from '../repository/batch';
import { Search } from '@fibl/zdb-common';
import { type Deferred, createDeferred } from '@/util/flow-helper';
import { getI18n } from '@/util/i18n';
import { watch } from 'vue';

export const useBatchStore = defineStore('batch', {
	state: () => ({
		currentJob: null as BatchRepo.BatchJob | null,
		jobPromise: null as Deferred<BatchRepo.BatchStatus> | null,
		jobStatus: {
			initial: 0,
			error: 0,
			success: 0,
			nochange: 0,
			status: 'new',
		} as BatchRepo.BatchStatus,
		templates: [] as BatchRepo.BatchTpl[],
	}),

	getters: {
		progress(): BatchRepo.BatchStatus {
			if (!this.jobStatus) return {} as BatchRepo.BatchStatus;
			return this.jobStatus;
		},
	},

	actions: {
		async runJob({
			template,
			config,
			attributeFilter,
			dryRun,
		}: {
			template: string;
			config: Record<string, any>;
			attributeFilter?: Search.SearchFilter;
			dryRun?: boolean;
		}) {
			if (template.length !== 36) {
				let tpl = await BatchRepo.getTemplateFromAlias(template);
				template = tpl.id;
			}
			const job = await BatchRepo.runJob({ template, config, attributeFilter, dryRun });
			return this.startWatchingJob(job);
		},

		resetJob() {
			this.currentJob = null;
		},

		waitForJob() {
			return this.jobPromise?.promise;
		},

		startWatchingJob(job: BatchRepo.BatchJob) {
			if (this.jobPromise) return this.jobPromise;
			this.currentJob = job;
			const jobId = this.currentJob.id;
			const prom = createDeferred<BatchRepo.BatchStatus>();
			const i18n = getI18n();
			const check = async () => {
				try {
					const res = await BatchRepo.getStatus(jobId);
					this.jobStatus = res;
					switch (res.status) {
						case 'new':
						case 'running': {
							if (this.currentJob) {
								setTimeout(check, 1000);
							} else {
								this.jobPromise = null;
							}
							break;
						}
						case 'aborted':
						case 'complete': {
							const jobDdata = await BatchRepo.getJob(jobId);
							this.currentJob = jobDdata;
							if (res.status === 'complete') prom.resolve(res);
							else prom.reject(new Error(i18n.t('stores.batchJobAborted')));
							this.jobPromise = null;
							break;
						}
						default: {
							prom.reject(
								new Error(i18n.t('stores.unknownStatus') + ': ' + res.status),
							);
						}
					}
				} catch (err: any) {
					console.error('error during check', err);
				}
			};
			check();
			this.jobPromise = prom;
			return prom.promise;
		},

		async loadLastJob() {
			try {
				if (!this.currentJob?.id) return;
				let jobId = this.currentJob.id;
				let job = (await BatchRepo.getJob(jobId)) as BatchRepo.BatchJob;
				if (['running', 'queued'].includes(job!.status)) {
					this.startWatchingJob(job);
				}
			} catch (e) {
				console.log('cannot load last job', e);
			}
		},

		waitForStatus(status: BatchRepo.BatchStatus['status'][]) {
			return new Promise<void>((resolve) => {
				if (status.includes(this.jobStatus.status)) {
					resolve();
					return;
   				}
				const stop = watch(
					() => this.jobStatus.status,
					(s) => {
						if (status.includes(s)) {
							stop();
							resolve();
						}
					}
				);
			});
		}
	},
});
